﻿//==========================================================================================================
// Copyright (C) NoreTechnology AB. All Rights Reserved.
//==========================================================================================================

// This file contains a component with the customers VersionInfo.

// ----------------------------------------------------------------------------------------------------
// Imports
// ----------------------------------------------------------------------------------------------------

import { applyThemeStyles } from "~base/Styling";
import classnames from "classnames";
import * as React from "react";
import { createUseStyles } from "react-jss";
import { ConsilioBaseTheme } from "~base/Styling/ConsilioTheme";
import {
    useDiscoveryGetAPIs,
    GetAPIs,
} from "~base/Services/API/Discovery/GetAPIs";
import { ApiStatus } from "~base/Services/API";
import { useSettings } from "~base/Settings/SettingsContext";
import { JssClass } from "~base/Styling";
import { ContainerProps } from "~base/Components/Layout/Container";
import { useAuthToken } from "~base/Services/API/AuthToken";

export interface VersionInfoStyles {
    p?: JssClass;
}

interface VersionInfoClasses {
    p?: string;
}

const useStyles: () => VersionInfoClasses = createUseStyles(
    (theme: ConsilioBaseTheme) => ({
        p: applyThemeStyles(theme.components.composite.versionInfo.p, {
            whiteSpace: "pre-line",
            textAlign: "left",
            color: "#9c9c9c",
            fontSize: "11px",
            fontFamily: theme.constants.defaultFont,
        }),
    }),
    { name: "VersionInfo" }
);

// ----------------------------------------------------------------------------------------------------
// Props
// ----------------------------------------------------------------------------------------------------

interface VersionInfoProps extends ContainerProps {
    className?: string;
}

// ----------------------------------------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------------------------------------

export const VersionInfo: React.FunctionComponent<VersionInfoProps> = (
    props: VersionInfoProps
) => {
    const [version, setVersion] = React.useState<string>(null);
    const { className, onScroll, ...containerProps } = props;

    const settings = useSettings();
    const displayVersionInfo = settings.valueAsBool("dev.displayVersionInfo");
    const [_, token] = useAuthToken({});

    const api = useDiscoveryGetAPIs({
        call: token !== null && version === null && displayVersionInfo === true,
        callback: async (
            status: ApiStatus,
            _: Response,
            responseData: GetAPIs
        ) => {
            setVersion(responseData?.SystemVersion ?? "missing system version");
        },
    });

    const classes = useStyles();
    return version ? (
        <p {...containerProps} className={classnames(className, classes.p)}>
            {version}
        </p>
    ) : null;
};

export default VersionInfo;
